import { useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';

export interface PriceBookReorderItem {
  id: string;
  name: string;
}

/**
 * The id at a slice position. A slice index always points at a real item (the sortable list is
 * built from the same slice), so a miss is a programming error — it throws instead of being
 * silently swallowed by `indexOf('') === -1`.
 */
export function requireSliceId(items: readonly { id: string }[], index: number): string {
  const id = items[index]?.id;
  if (!id) throw new Error(`Price book reorder: no item at slice position ${index} of ${items.length}.`);
  return id;
}

export interface PriceBookReorder {
  announcement: string;
  moveById: (activeId: string, overId: string) => void;
  moveUp: (id: string) => void;
  moveDown: (id: string) => void;
  canMoveUp: (id: string) => boolean;
  canMoveDown: (id: string) => boolean;
}

/**
 * Reorders the FULL id list (every sheet or every line item of a sheet, not just the paginated
 * slice) and commits the new order once. Drag/keyboard moves arrive via `moveById`; menu moves via
 * `moveUp` / `moveDown`. Each commit is announced through `announcement`.
 */
export function usePriceBookSheetReorder(items: PriceBookReorderItem[], onCommit: (orderedIds: string[]) => void): PriceBookReorder {
  const [announcement, setAnnouncement] = useState('');
  const indexOf = (id: string) => items.findIndex((item) => item.id === id);

  const move = (fromIndex: number, toIndex: number) => {
    const item = items[fromIndex];
    if (!item || fromIndex === toIndex || toIndex < 0 || toIndex >= items.length) return;
    onCommit(arrayMove(items, fromIndex, toIndex).map((entry) => entry.id));
    setAnnouncement(`Moved ${item.name} to position ${toIndex + 1} of ${items.length}.`);
  };

  return {
    announcement,
    moveById: (activeId, overId) => {
      if (!activeId || !overId) throw new Error('Price book reorder: moveById needs two non-empty ids.');
      move(indexOf(activeId), indexOf(overId));
    },
    moveUp: (id) => move(indexOf(id), indexOf(id) - 1),
    moveDown: (id) => move(indexOf(id), indexOf(id) + 1),
    canMoveUp: (id) => indexOf(id) > 0,
    canMoveDown: (id) => {
      const index = indexOf(id);
      return index >= 0 && index < items.length - 1;
    }
  };
}
